import MidiEffect from './MidiEffect.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import StepData from 'pattern/StepData.js';
import MidiTrigger from './MidiTrigger.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Resolution from 'engine/Resolution.js';
import OptionParameter from 'parameters/OptionParameter.js';
import {UP, DOWN, UP_DOWN, DOWN_UP} from './constants.js';

class ArpEffect extends MidiEffect {
   constructor(track) {
       super('Arpeggiator', track);

       this.notesToRemove = [];

        // how long the warp cycle goes for (in bars). effectively how
        // long it actually sounds
        this.arpRateParameter = new OptionParameter(
            'arpRate', this, (o, x) => x,
            {
                documentation: "The rate or speed of the arpeggiation, synced to the project tempo.",
                min: 0,
                prettyName: "Rate",
                max: 1,
                unit: '', 
                options: {
                    0: new Resolution(4, false),
                    0.1: new Resolution(4, true),
                    0.2: new Resolution(8, false),
                    0.3: new Resolution(8, true),
                    0.4: new Resolution(16, false),
                    0.5: new Resolution(16, true),
                    0.6: new Resolution(32, false),
                    0.7: new Resolution(32, true),
                },
                defaultValue: new Resolution(16, false)
            }
        );

        this.registerParameter(this.arpRateParameter);
        this.arpOctavesParameter = new AbstractParameter(
            'arpOctaves', this, (o, x) => x,
            {
                documentation: "The number of octaves to cycle through.",
                prettyName: "Octaves",
                min: 0,
                max: 8,
                unit: '', 
                defaultValue: 1,
            });
        this.registerParameter(this.arpOctavesParameter);
        
        this.arpDirectionParameter = new AbstractParameter(
            'arpDirection', this, (o, x) => x,
            {
                min: DOWN,
                documentation: "The direction we play notes in.",
                prettyName: "Direction",
                max: UP,
                unit: '',
                defaultValue: DOWN 
            });

        this.registerParameter(this.arpDirectionParameter);

        this.arpDurationFactorParameter = new AbstractParameter(
            'arpDurationFactor', this, (o, x) => x,
            {
                documentation: "Multiples the duration of each trigger by this number.",
                min: 0.0001,
                prettyName: "Duration Factor",
                max: 8,
                unit: 'x',
                defaultValue: 1 
            });

        this.registerParameter(this.arpDurationFactorParameter);

        this.arpRepeatParameter = new AbstractParameter(
            'arpRepeatFactor', this, (o, x) => x,
            {
                documentation: "The number of triggers per note in the cycle.",
                min: 1,
                max: 8, 
                unit: 'x',
                prettyName: "Repeats",
                defaultValue: 1 
            });

        this.registerParameter(this.arpRepeatParameter);
        
        // state for the arpeggiator
        this.noteToDuration = {};
        this.notes = [];
        this.index = 0;
        this.repeatIndex = 0;
        this.currentOctave = 0;
    }

    getVisualizedParameters() {
        return [this.arpRateParameter];
    }

    getText() {
        return this.arpRateParameter.get().toString();
    }

    get documentation() {
        return "Cycle through notes in a chord at a rate set by the rate parameter.";
    }

    isRealTime() {
        return false;
    }

    get arpRate() {
        return this.arpRateParameter.get();
    }

    set arpRate(rate) {
        this.arpRateParameter.set(rate);
    }

    get arpOctaves() {
        return this.arpOctavesParameter.get();
    }

    get arpDirection() {
        return this.arpDirectionParameter.get();
    }

    get arpDurationFactor() {
        return this.arpDurationFactorParameter.get();
    }

    get arpRepeat() {
        return this.arpRepeatParameter.get();
    }

    // overrriding so that we don't clear on each new step
    onNewStep() {}
    
    clear() {
        this.noteToDuration = {};
        this.notesToRemove = [];
        this.notes = [];
        this.repeatIndex = 0;
        this.index = 0;
        if (this.arpDirection === DOWN) {
            this.currentOctave = this.arpOctaves;
        } else {
            this.currentOctave = 0;
        }
    }

    // overriden method
    input(trigger, isLive=false) {
        let duration = trigger.stepData.duration;
        let transpose = trigger.stepData.transpose;

        if (!isLive) {
            if (transpose in this.noteToDuration) {
                this.noteToDuration[transpose] = this.noteToDuration[transpose] + duration * 2;
            } else {
                this.noteToDuration[transpose] = duration * 2;
            }
        }

        if (this.getIndexOfTrigger(trigger) < 0) {
            trigger.stepData = this.getStep(trigger.stepData, transpose, duration, false);
            this.notes.push(trigger);
            this.notes = this.notes.sort(
                (a,b) => a.stepData.transpose - b.stepData.transpose);

            if (this.arpDirection === DOWN) {
                this.notes.reverse();
            }
        }
    }

    // overriden method
    output(time, stepNumber, actualStepNumber) {
        let output = this.getNextTriggers(stepNumber)
            .map(trigger => {
                trigger.time += time;
                trigger.stepNumber = actualStepNumber;
                return trigger;
            });

        this.notesToRemove.forEach(
            t => this.actuallyRemoveNote(t));
        this.notesToRemove = [];

        return output;
    }

    getIndexOfTrigger(trigger) {
        for (let i in this.notes) {
            if (this.notes[i].stepData.transpose === trigger.stepData.transpose) {
                return i;
            }
        }

        return -1;
    }

    actuallyRemoveNote(transpose) {
        let index = -1;
        for (let i in this.notes) {
            if (transpose === this.notes[i].stepData.transpose) {
                index = i;
                break;
            }
        }
        if (index > -1) {
            this.notes.splice(index, 1);
        }

        if (this.index >= this.notes.length) {
            this.index = 0;
            this.currentOctave = 0;
        }

        if (this.notes.length === 0) {
            this.clear();
        }
    }

    removeNote(transpose) {
        this.notesToRemove.push(transpose);
    }

    nextNote(octaves) {
        if (this.notes.length == 0) {
            return undefined;
        }

        let trigger = this.notes[this.index];
        let note = trigger.stepData.transpose;
        note = note + 12 * this.currentOctave;
        this.repeatIndex++;
        
        if (this.repeatIndex >= this.arpRepeat) {
            this.repeatIndex = 0;
            this.index++;
        }
        if (this.index >= this.notes.length) {
            this.index = 0;
            if (this.arpDirection === UP) {
                this.currentOctave = (this.currentOctave + 1) % (octaves + 1);
            } else if (this.arpDirection === DOWN) {
                this.currentOctave = (this.currentOctave - 1) % (octaves + 1);
                if (this.currentOctave < 0) {
                    this.currentOctave = octaves;
                }
            }
        } 

        let stepData = this.getStep(
            trigger.stepData,
            note, 
            this.track.trackData.duration * this.arpDurationFactor);
        return new MidiTrigger(stepData, trigger.time, trigger.stepNumber);
    }

    getNextTriggers(stepNumber) {
        let triggerTimes = getTriggerTimes(stepNumber, this.arpRate);
        let triggers = triggerTimes.map(
            triggerTime => {
                let trigger = this.nextNote(this.arpOctaves);
                if (trigger !== undefined) {
                    trigger.time = triggerTime.time;
                }
                return trigger;
            })
            .filter(
                trigger =>
                    trigger !== undefined);

        if (window.timeB === undefined) {
            window.timeB = 0;
            window.timeBCount = 0;
        }

        // now reduce the duration of each note by 1 and delete
        // if needed
        for (let note in this.noteToDuration) {
            this.noteToDuration[note] -= 1;
            if (this.noteToDuration[note] <= 0) {
                delete this.noteToDuration[note];
                this.removeNote(parseInt(note));
            }
        }

        if (Object.keys(this.noteToDuration).length === 0 &&
            this.notes.length === 0) {
            this.clear();
        }

        return triggers;
    }

    getCopy(track, ignoreId=true) {
        let arp = new ArpEffect(track);
        arp.fromJson(this.getJson(), ignoreId);
        return arp;
    }

    getJson() {
        return {
            ...super.getJson(),
            arpRate: this.arpRate.getJson()
        };
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(
            this.arpRateParameter,
            new Resolution(16, false)
                .fromJson(json.arpRate),
            bulk);
        return bulk;
    }

    getCopy(track) {
        let warpEffect = new ArpEffect(track);
        warpEffect.fromJson(this.getJson(), true);
        return warpEffect;
    }
}



export default ArpEffect;
